import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Check, ChevronDown, Plus, Loader2 } from "lucide-react";
import { handleApiError } from "../utils/apiErrors";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { useGetWorkspacesQuery } from "../store/api/workspaces";
import { useSwitchWorkspaceMutation } from "../store/api/auth";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { setCurrentWorkspace, setWorkspaces } from "../store/slices/workspaceSlice";
import { WorkspaceWithRole } from "../types";

export default function WorkspaceSelector() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { currentWorkspace, workspaces } = useAppSelector((state) => state.workspace);
  
  // Fetch workspaces from API
  const { data: apiWorkspaces, isLoading, isError } = useGetWorkspacesQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [switchWorkspaceApi] = useSwitchWorkspaceMutation();
  
  // Update Redux store when API data changes
  useEffect(() => {
    if (apiWorkspaces) {
      dispatch(setWorkspaces(apiWorkspaces));
    }
  }, [apiWorkspaces, dispatch]);

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return t('roles.admin', 'Admin');
      case 'member':
        return t('roles.member', 'Member');
      case 'guest':
        return t('roles.guest', 'Guest');
      default:
        return role;
    }
  };

  const handleSwitchWorkspace = async (workspace: WorkspaceWithRole) => {
    try {
      // Call API to update user's current workspace in database
      await switchWorkspaceApi({ workspace_id: workspace.id }).unwrap();
      // Update Redux state and localStorage
      dispatch(setCurrentWorkspace(workspace));
      // Reload the page to refresh all data with new workspace context
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch workspace:', error);
      handleApiError(error, t('workspace.switchError', 'Failed to switch workspace'));
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
        <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
        <span className="text-sm text-gray-500">{t('common.loading', 'Loading...')}</span>
      </div>
    );
  }

  // Error state or no workspaces
  if (isError || !currentWorkspace) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
        <Building2 className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-500">{t('workspace.noWorkspace', 'No workspace')}</span>
      </div>
    );
  }

  // Single workspace - simple display without dropdown
  if (workspaces.length <= 1) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
          <Building2 className="w-4 h-4 text-white" />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900">{currentWorkspace.name}</span>
          <span className="text-xs text-gray-500">{getRoleLabel(currentWorkspace.role)}</span>
        </div>
      </div>
    );
  }

  // Multiple workspaces - show dropdown
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="flex items-center gap-2 h-auto px-3 py-2 hover:bg-gray-50"
        >
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <Building2 className="w-4 h-4 text-white" />
          </div>
          <div className="flex flex-col items-start">
            <span className="text-sm font-medium text-gray-900">{currentWorkspace.name}</span>
            <span className="text-xs text-gray-500">{getRoleLabel(currentWorkspace.role)}</span>
          </div>
          <ChevronDown className="w-4 h-4 ml-2 text-gray-500" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <div className="px-2 py-1.5 text-xs text-gray-500 font-medium">
          {t('workspace.selectWorkspace', 'Select Workspace')}
        </div>
        {workspaces.map((workspace:any) => {
          const isCurrentWorkspace = workspace.id === currentWorkspace.id;
          
          return (
            <DropdownMenuItem
              key={workspace.id}
              onClick={() => !isCurrentWorkspace && handleSwitchWorkspace(workspace)}
              className={`flex items-center gap-3 px-2 py-2 cursor-pointer ${
                isCurrentWorkspace ? 'bg-blue-50' : ''
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                isCurrentWorkspace ? 'bg-blue-600' : 'bg-gray-200'
              }`}>
                <Building2 className={`w-4 h-4 ${
                  isCurrentWorkspace ? 'text-white' : 'text-gray-600'
                }`} />
              </div>
              <div className="flex-1">
                <div className="text-sm text-gray-900">{workspace.name}</div>
                <div className="text-xs text-gray-500">{getRoleLabel(workspace.role)}</div>
              </div>
              {isCurrentWorkspace && (
                <Check className="w-4 h-4 text-blue-600" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
